import { computed, signal } from '@lit-labs/signals';
import { connectFirestoreToEmulator, enableFirestoreOfflinePersistence, getFirestoreDb, } from '@df/firebase';
import { FirestoreCollectionStore } from './firestore-base.store.js';
import { Timestamp, collection, orderBy, } from 'firebase/firestore';
const FIRESTORE_HOST = '127.0.0.1';
const FIRESTORE_PORT = 8280;
const PAGE_SIZE = 20;
const defaultState = {
    status: 'idle',
    documents: [],
    error: null,
    isListening: false,
    lastUpdated: null,
    currentPage: 1,
    pageSize: PAGE_SIZE,
    hasNextPage: false,
    hasPreviousPage: false,
    queryDescription: 'Awaiting authentication',
};
const fallbackStateSignal = signal({ ...defaultState });
const storeSignal = signal(null);
const collectionRefSignal = signal(null);
const activeUserIdSignal = signal(null);
export const pushupCollectionState = computed(() => {
    const store = storeSignal.get();
    return store ? store.state.get() : fallbackStateSignal.get();
});
export const pushupSummaryState = computed(() => {
    const documents = pushupCollectionState.get().documents;
    const entryCount = documents.length;
    const lastEntryAt = entryCount > 0 ? documents[0].recordedAt ?? null : null;
    const byType = {
        pushups: { count: 0, totalValue: 0 },
        squats: { count: 0, totalValue: 0 },
        plank: { count: 0, totalValue: 0 },
        dumbbells: { count: 0, totalValue: 0 },
        bands: { count: 0, totalValue: 0 },
        hang: { count: 0, totalValue: 0 },
    };
    documents.forEach((entry) => {
        byType[entry.exerciseType].count += 1;
        byType[entry.exerciseType].totalValue += entry.value;
    });
    return {
        totalExercises: entryCount,
        entryCount,
        lastEntryAt,
        byType,
    };
});
export async function initializePushupStore(app, userId, useEmulator) {
    if (!userId) {
        throw new Error('A Firebase Auth user id is required to initialize the pushup store.');
    }
    if (userId === activeUserIdSignal.get() && storeSignal.get()) {
        return;
    }
    teardownPushupStore();
    const db = getFirestoreDb(app);
    if (useEmulator) {
        connectFirestoreToEmulator(db, {
            host: FIRESTORE_HOST,
            port: FIRESTORE_PORT,
        });
    }
    await enableFirestoreOfflinePersistence(db);
    const collectionPath = buildCollectionPath(userId);
    const ref = collection(db, collectionPath);
    const newStore = new FirestoreCollectionStore(ref, {
        defaultConstraints: [orderBy('recordedAt', 'desc')],
        defaultQueryDescription: 'Your activity history (newest first)',
        pageSize: PAGE_SIZE,
        mapDocument: normalizeEntry,
    });
    collectionRefSignal.set(ref);
    storeSignal.set(newStore);
    activeUserIdSignal.set(userId);
    await newStore.loadInitialPage();
    newStore.startRealtime();
}
export function teardownPushupStore() {
    const store = storeSignal.get();
    if (store) {
        store.stopRealtime();
    }
    storeSignal.set(null);
    collectionRefSignal.set(null);
    activeUserIdSignal.set(null);
    fallbackStateSignal.set({ ...defaultState });
}
export async function logPushupEntry(draft) {
    // Convert legacy pushup format to new exercise format
    const exerciseDraft = {
        exerciseType: 'pushups',
        value: draft.count,
        note: draft.note,
        recordedAt: draft.recordedAt,
    };
    return logExerciseEntry(exerciseDraft);
}
export async function logExerciseEntry(draft) {
    if (!Number.isFinite(draft.value)) {
        throw new Error('Exercise value must be a valid number.');
    }
    const value = Math.max(0, Math.trunc(draft.value));
    if (value <= 0) {
        throw new Error('Exercise value must be greater than zero.');
    }
    const now = new Date();
    const recordedAt = draft.recordedAt ?? now;
    const payload = {
        exerciseType: draft.exerciseType,
        value,
        note: normalizeNote(draft.note),
        recordedAt,
        createdAt: now,
        updatedAt: now,
        ownerId: ensureActiveUserId(),
    };
    const activeStore = ensureStore();
    const id = await activeStore.create(payload);
    await activeStore.refresh();
    return id;
}
export async function deletePushupEntry(id) {
    const activeStore = ensureStore();
    await activeStore.delete(id);
    await activeStore.refresh();
}
export async function refreshPushupEntries() {
    const activeStore = ensureStore();
    await activeStore.refresh();
}
export function getActivePushupCollectionPath() {
    const ref = collectionRefSignal.get();
    return ref?.path ?? null;
}
function normalizeEntry(entry) {
    const recordedAt = entry.recordedAt instanceof Timestamp ? entry.recordedAt.toDate() : entry.recordedAt ?? null;
    const createdAt = entry.createdAt instanceof Timestamp ? entry.createdAt.toDate() : entry.createdAt ?? null;
    const updatedAt = entry.updatedAt instanceof Timestamp ? entry.updatedAt.toDate() : entry.updatedAt ?? null;
    return {
        ...entry,
        recordedAt,
        createdAt,
        updatedAt,
    };
}
function ensureStore() {
    const store = storeSignal.get();
    if (!store) {
        throw new Error('Exercise store has not been initialized. Call initializePushupStore() after authentication.');
    }
    return store;
}
function ensureActiveUserId() {
    const userId = activeUserIdSignal.get();
    if (!userId) {
        throw new Error('Pushup actions require an authenticated user.');
    }
    return userId;
}
function normalizeNote(note) {
    if (typeof note !== 'string') {
        return null;
    }
    const trimmed = note.trim();
    return trimmed.length ? trimmed : null;
}
function buildCollectionPath(userId) {
    return `activity/${userId}/pushups`;
}
//# sourceMappingURL=activity-log.store.js.map